<script setup lang="ts">
import type { MatchMode, SortieSettlement } from '@shared/protocol';
import { TUTORIAL_ARENA_ID } from '@shared/protocol';
import { MAP_SUMMARIES } from '@shared/maps';
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';

import JoinScreen from './ui/JoinScreen.vue';
import LandingScreen from './landing/LandingScreen.vue';
import VerifyEmailGate from './ui/VerifyEmailGate.vue';
import VerifyEmailScreen from './ui/VerifyEmailScreen.vue';
import ResetPasswordScreen from './ui/ResetPasswordScreen.vue';
import ProfileOverlay from './profile/ProfileOverlay.vue';
import GarageScreen from './ui/GarageScreen.vue';
import GameHud from './ui/GameHud.vue';
import LoadingScreen from './ui/LoadingScreen.vue';
import MusicControl from './ui/MusicControl.vue';
import ConfirmSignOut from './ui/ConfirmSignOut.vue';
import RotatePrompt from './ui/RotatePrompt.vue';
import FullscreenPrompt from './ui/FullscreenPrompt.vue';
import { Connection } from './net/connection';
import type { ArenaScene, HudState } from './game/arena-scene';
// A value import from `game/`, unlike the type-only line above, and safe to be
// one: `model-availability.ts` is two `fetch`es and a memo with no Babylon
// behind it, so it does not drag the engine back into the entry chunk.
import { resetModelAvailability } from './game/model-availability';
import { api } from './net/api';
import { seedPreviewGarage, useGarage } from './state/garage';
import { useSession } from './state/session';
import { showNotice } from './state/notification-banner';
import { handheld } from './state/device';
import { clearDeepLink, readDeepLink, type DeepLink } from './state/deep-link';
import { readNewsRoute, type NewsRoute } from './state/news-route';
import { readAdminRoute, type AdminSection } from './state/admin-route';
import { readIsNotFound } from './state/not-found';
import AdminScreen from './admin/AdminScreen.vue';
import NewsListPage from './news/NewsListPage.vue';
import NewsArticlePage from './news/NewsArticlePage.vue';
import NotFoundScreen from './ui/NotFoundScreen.vue';
import { useAccount } from './state/account';
import { enterImmersive, exitImmersive } from '@/state/immersive';
import { worstCaseResultHud } from '@/dev/hud-preview';
import { wantsTouch } from '@/game/input-source';

/**
 * Three phases: sign in, garage, arena.
 *
 * The garage is the home screen — a player who is signed in lands there, picks
 * a loadout, and deploys from it. Leaving the arena comes back to it rather
 * than to the sign-in form, because the session outlives the sortie.
 *
 * The sign-in phase has two faces, and the landing page is the one it opens on.
 * A visitor who has never played is shown what the game *is* before being asked
 * for an email — `authView`, below. There is no router in this app and this is
 * not the place to introduce one: two views with one transition between them is
 * a boolean, and making it a route would put the arena's canvas behind a history
 * stack for the sake of a marketing page.
 *
 * **Two phases were added with the profile screen, and both are gates rather
 * than destinations.**
 *
 * `link` is what the two emails point at — `/verify-email?token=` and
 * `/reset-password?token=`. It outranks every other phase including `restoring`,
 * because the URL is a complete instruction that needs no session to carry out
 * and the alternative was what shipped before: the token silently dropped on the
 * landing page. Still not a router — one read of the URL at start-up and a
 * `replaceState` to forget it. See `state/deep-link.ts`.
 *
 * `unverified` is the client half of the verification gate. It sits between
 * `auth` and `garage`, and the server half is `EmailVerifiedGuard`, which refuses
 * the garage, the economy, the battle lobby and the arena ticket outright — so
 * deleting this branch does not open the game, it only replaces one honest screen
 * with a garage whose every control fails.
 */
const session = useSession();
const garage = useGarage();

/**
 * The tab title, landing page vs. everything behind sign-in.
 *
 * `document.title` on mount is whatever `meta-inject` rendered server-side for
 * a stranger arriving at `/` — the marketing title crawlers and a shared link
 * see, set from the admin panel's Meta Tags. That is right for the landing
 * page and wrong everywhere else: once a player is behind sign-in there is no
 * marketing left to do, and the tab should say what is actually open rather
 * than repeat the pitch. Captured once, before the watcher below can touch it,
 * so signing out returns the exact string the server sent rather than a
 * client-side guess at what it was.
 */
const landingTitle = document.title;
const IN_GAME_TITLE = 'Steel of War - Game';

watch(
  () => session.signedIn.value,
  (signedIn) => {
    document.title = signedIn ? IN_GAME_TITLE : landingTitle;
  },
  { immediate: true },
);

const inArena = ref(false);

/**
 * Which face the sign-in phase is wearing.
 *
 * Opens on the landing page, and `Play free` swaps in the form. It resets to
 * `landing` whenever the phase becomes `auth` — see the watcher below — so
 * signing out returns to the page rather than dropping the player straight onto
 * a login box, which reads as having been thrown out rather than let go.
 */
const authView = ref<'landing' | 'form'>('landing');

/**
 * The email link this page was opened with, if any.
 *
 * Read once, synchronously, before anything else runs — a token is what the URL
 * *is*, and re-reading `location` later would pick up the `replaceState` that
 * clears it. Cleared by `leaveDeepLink`, which is also what puts the address bar
 * back to `/`.
 */
const deepLink = ref<DeepLink | null>(readDeepLink());

/**
 * `/news` and `/news/:slug` — see `state/news-route.ts`. Read on mount
 * rather than at module scope like `deepLink`, because unlike a one-shot
 * token this route is navigated *within* the running app (the footer, the
 * lintel, "Back to news"), so it has to keep re-reading the URL rather than
 * settling once at start-up. `popstate` covers the browser's own back and
 * forward buttons; `navigateNews`/`leaveNewsRoute` dispatch the same event
 * themselves so a programmatic navigation is indistinguishable from one.
 */
const newsRoute = ref<NewsRoute | null>(readNewsRoute());

/**
 * `/admin` and `/admin/<section>` — see `state/admin-route.ts`.
 *
 * Re-read on `popstate` alongside the news route, for the same reason and by
 * the same listener: the panel navigates within the running app, so a URL read
 * once at start-up would leave the rail dead.
 *
 * `/admin` used to be reverse-proxied to the Kottster container and never
 * reached the SPA at all. Kottster is gone; Caddy's catch-all now serves this.
 */
const adminRoute = ref<AdminSection | null>(readAdminRoute());

/**
 * Whether the current URL isn't one of this app's own routes at all — see
 * `state/not-found.ts`. Read alongside the other two below, on the same
 * `popstate` listener, for the same reason: a URL can change under a
 * running app (a typo'd link opened in this tab, the back button) and
 * nothing else here re-derives it.
 */
const notFound = ref(readIsNotFound());

function onNewsPopState(): void {
  newsRoute.value = readNewsRoute();
  adminRoute.value = readAdminRoute();
  notFound.value = readIsNotFound();
}

onMounted(() => window.addEventListener('popstate', onNewsPopState));
onBeforeUnmount(() => window.removeEventListener('popstate', onNewsPopState));

/** Whether the profile overlay is up. It is reachable from two screens. */
const profileOpen = ref(false);
const account = useAccount();

const busy = ref(false);
const hud = ref<HudState>({ speedKph: 0, armourPercent: 0, latencyMs: 0, fps: 0, playerCount: 0, players: [] });
/**
 * The arena we are actually in, as the server named it.
 *
 * Shown in the HUD because with several arenas running "which battle am I in"
 * stops being obvious — and because a request for one that had just been reaped
 * lands in quick match instead, which the player has to be able to see.
 */
const arenaName = ref<string | null>(null);

const canvas = ref<HTMLCanvasElement | null>(null);
const connection = shallowRef<Connection | null>(null);
/**
 * What the last deliberately-abandoned sortie paid, straight from the server.
 *
 * Non-null only between pressing Leave in a running battle and pressing Return to
 * garage on the receipt. See `requestLeave`.
 */
const settlement = shallowRef<SortieSettlement | null>(null);
const scene = shallowRef<ArenaScene | null>(null);

/**
 * Whether the loading screen is still up.
 *
 * Raised the moment the arena phase begins and lowered by
 * `ArenaScene.whenReady()` — meshes, scenery and the first snapshot — so it
 * covers exactly the window in which the battlefield exists but is not worth
 * looking at. See `LoadingScreen.vue`.
 */
const loading = ref(false);
/** The map being loaded, named on the loading screen when the picker chose one. */
const loadingSubtitle = ref<string | null>(null);
/**
 * Whether the deploy being waited on is the tutorial, which gets the controls card.
 *
 * Derived from the arena id rather than passed in as a second argument, because
 * the sentinel *is* the fact — `startTutorial` is an ordinary `deploy` with
 * `TUTORIAL_ARENA_ID`, and a separate flag would be a second way to say the same
 * thing that could disagree with it.
 */
const loadingTutorial = ref(false);

/**
 * Whether the escape menu is up — which is also the only time the arena shows a
 * mouse cursor.
 *
 * Pointer lock hides the cursor by itself, but only while it holds it, and it
 * is deliberately allowed to fail (`docs`: "its refusal is swallowed — aiming
 * still works unlocked"). So a player whose browser refuses the lock, or who has
 * just dismissed it, was left with an arrow sitting over the gunsight. Hiding it
 * in CSS covers both cases without making pointer lock load-bearing for
 * something that is purely cosmetic.
 */
const menuOpen = ref(false);

/**
 * The shortest time the loading screen may be up, in ms.
 *
 * Not padding for its own sake: with a warm cache `whenReady` can settle inside
 * a couple of frames, and a full-screen image that appears and vanishes is a
 * flash of unexplained artwork rather than a loading screen. Half a second is
 * long enough to read as deliberate and short enough not to be a tax.
 */
const MIN_LOADING_MS = 500;

/**
 * How long to wait before giving up and showing the arena anyway.
 *
 * The gate cannot reject, but it *can* fail to settle — a snapshot that never
 * arrives, or an asset wedged behind a stalled connection. Being dropped into a
 * half-drawn arena is a bad outcome; being held on a loading screen forever is a
 * worse one, because the player cannot even leave.
 */
const LOADING_TIMEOUT_MS = 20000;

/**
 * `/win` and `/defeat` mount the result screen straight away, with a
 * synthetic worst-case roster, so a layout or copy change is a page load away
 * rather than a played-out battle. Read once at startup rather than as a
 * `computed` — a preview is a fixed thing to look at, not a live route.
 *
 * `import.meta.env.DEV` is a build-time constant Vite inlines and
 * dead-code-eliminates, the same guarantee that already gates the dev
 * sign-in prefill (see `CLAUDE.md`'s Dev defaults) — `vite build` never
 * emits this branch, so the path cannot be reached from a production
 * deploy even by a stranger typing the URL. It stays reachable under `yarn
 * dev` with no session, no socket and no account, which is the point.
 */
// The phone layout when the arena would pick touch input here (a handheld, or
// DevTools emulating one); `?touch=1` / `?touch=0` forces either.
const previewTouch =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('touch')
    ? new URLSearchParams(window.location.search).get('touch') !== '0'
    : wantsTouch();
const previewHud =
  import.meta.env.DEV && typeof window !== 'undefined'
    ? window.location.pathname === '/win'
      ? worstCaseResultHud({ mode: 'team-battle', won: true, touch: previewTouch })
      : window.location.pathname === '/defeat'
        ? worstCaseResultHud({ mode: 'team-battle', won: false, touch: previewTouch })
        : null
    : null;

if (previewHud) seedPreviewGarage();

function leavePreview(): void {
  window.location.href = '/';
}
function noop(): void {}

const phase = computed<
  'not-found' | 'admin' | 'news' | 'link' | 'restoring' | 'auth' | 'unverified' | 'garage' | 'playing'
>(() => {
  // Outranks everything, including a session that hasn't resolved yet — an
  // unrecognised URL is wrong regardless of who is or isn't signed in, and
  // by construction (`isKnownPath`) this is only ever true when none of the
  // three route reads below could have matched anyway.
  if (notFound.value) return 'not-found';
  /*
   * The admin panel outranks everything except a session that has not resolved
   * yet, and it is the one phase that deliberately waits: unlike news it is not
   * public, and unlike a deep link the URL is not itself a credential. So an
   * operator who hard-refreshes `/admin` gets "Checking your papers…" and then
   * the panel, rather than a flash of the sign-in form before the cookie has
   * been checked.
   *
   * The screen renders for a signed-in *non*-admin too, and says so — see
   * `AdminScreen`'s note on why that is better than pretending the URL is not
   * there. It is not a security decision either way: the API checks the role on
   * every request.
   */
  if (adminRoute.value && !session.restoring.value && session.signedIn.value) return 'admin';
  // News is public, shareable content — it must render for a stranger with
  // no session at all, so it outranks every other phase including `link`
  // and `restoring`. The two can never actually coincide (the paths don't
  // overlap), but checking this first is the same discipline as `link`
  // outranking `restoring`: a URL is a complete instruction and does not
  // wait on anything.
  if (newsRoute.value) return 'news';
  // First, and ahead of `restoring`: a token identifies the account by itself, so
  // there is nothing to wait for a session to tell us.
  if (deepLink.value) return 'link';
  if (session.restoring.value) return 'restoring';
  if (!session.signedIn.value) return 'auth';
  // Read from the session record, which core's auth refreshes from the database
  // on every request — so confirming in another tab lifts this without a reload.
  if (session.player.value?.emailVerified === false) return 'unverified';
  return inArena.value ? 'playing' : 'garage';
});

/**
 * Done with the link screen.
 *
 * Clears the URL *and* the state, in that order, and re-derives the session: a
 * verification that succeeded has changed `emailVerified` on the server, and
 * `VerifyEmailScreen` has already refreshed the cookies, so this simply drops the
 * app back onto whichever phase is now correct — the garage for somebody who
 * verified in the browser they registered in, the sign-in form for anybody else.
 */
function leaveDeepLink(): void {
  clearDeepLink();
  deepLink.value = null;
}

onMounted(() => {
  session.restore();
});

/**
 * Warms the arena chunk once a real, signed-in, verified player reaches the
 * garage — never for an anonymous visitor reading the landing page.
 *
 * `warmArenaScene()` used to run unconditionally from `onMounted` above,
 * which fetched the whole battle engine for 100% of visitors regardless of
 * whether they ever sign in, on the strength of gating only on
 * `handheld()`/connection type. The intent stated in its own comment — warm
 * the chunk before a signed-in player reaches the garage, since the hangar
 * needs `tank.ts` too — was always about `phase === 'garage'`, not about
 * mount. `immediate: true` covers a session that restores straight into the
 * garage; re-signing in later fires it again, which is harmless since
 * `loadArenaScene()`'s `import()` resolves from the module cache after the
 * first real fetch.
 */
watch(
  phase,
  (p) => {
    if (p === 'garage') warmArenaScene();
  },
  { immediate: true },
);

// Signing out mid-sortie has to take the socket with it.
watch(
  () => session.signedIn.value,
  (signedIn) => {
    if (!signedIn) {
      leave();
      garage.reset();
      // The profile store outlives the session, so without this the next account
      // to sign in on this tab would see the previous one's email and invoices
      // for the moment before the first fetch landed.
      account.clear();
      profileOpen.value = false;
      // Back to the landing page, not to the form. Also covers the session
      // simply expiring, where the player did not ask to be anywhere.
      authView.value = 'landing';
    }
  },
);

/**
 * Into the arena.
 *
 * `arenaId` is optional and unset means quick match — so the sidebar's Deploy
 * button reaches this with no argument and gets precisely the behaviour it
 * always had. Only the Arena tab ever passes one.
 *
 * `mapId` is the garage picker's answer, and it only matters when no arena was
 * named: an arena's map is fixed when the arena is made, so naming a room names
 * its map. Whatever the server decides, it says so in `joined`, and everything
 * that draws or collides reads it from there rather than from this argument.
 */
/**
 * Fetches the arena's code, the first time a player deploys.
 *
 * **A type-only import above and a dynamic one here, deliberately.** Statically
 * imported, `ArenaScene` drags the whole of Babylon, `tank.ts`, `vfx.ts` and
 * `effects.ts` into the entry chunk — which measured **5.3 MB, 1.36 MB
 * gzipped** — so a stranger who lands on the marketing page and never signs in
 * downloaded and parsed the entire game before it painted. `HangarScene`,
 * `hero-scene` and `model-thumbnail` were already split out for the same
 * reason; this is the last and by far the largest of them.
 *
 * It costs nothing at the point of use: `deploy` is already several awaits long
 * and already raises the loading screen before the scene is constructed, so the
 * fetch overlaps the arena-ticket round trip and lands inside a window the
 * player is already watching artwork through.
 *
 * Kicked off *beside* the ticket request rather than after it, so the two are
 * concurrent, and cached module-side by the browser so the second deploy of a
 * session pays nothing at all.
 */
type ArenaSceneCtor = typeof import('./game/arena-scene')['ArenaScene'];

function loadArenaScene(): Promise<ArenaSceneCtor> {
  return import('./game/arena-scene').then((m) => m.ArenaScene);
}

/**
 * Fetches and evaluates that chunk on idle, once the app has painted.
 *
 * **This is the other half of the split, and without it the split is a
 * regression rather than a win.** Statically imported, the engine was parsed
 * during the initial page load, so by the time anybody reached the garage it
 * was in memory and the hangar's own dynamic import resolved from cache.
 * Splitting it out moved that cost to the first thing that needs it — which is
 * the *hangar*, not the arena, because `HangarView` pulls `tank.ts` too. Caught
 * by `garage-hangar`, which measured the room at 432 triangles against a
 * baseline of 1,047,460: the case was not wrong, the hangar really had not
 * finished loading.
 *
 * So the work is not removed, it is moved off the critical path: nothing is
 * parsed before the landing page paints, and everything is parsed before a
 * signed-in player has finished reading the sign-in form. The entry chunk went
 * from 5.96 MB to 0.56 MB (1,381 kB to 182 kB gzipped) and this is what keeps
 * that from costing the garage anything.
 *
 * **Skipped on a handheld and on a metered connection**, which is the whole
 * reason it is a policy and not an unconditional `import()`. Speculatively
 * pulling megabytes of engine over cellular for a visitor who may only be
 * reading the landing page is the opposite of what the split was for. There the
 * chunk is fetched when it is actually needed, and the loading screen — which is
 * gated on readiness rather than on a timer — already covers the wait.
 *
 * Failure is silence: this is a cache warm-up, so a chunk that does not arrive
 * must not raise anything. `deploy` asks again and reports its own failure.
 */
function warmArenaScene(): void {
  if (handheld()) return;
  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection;
  if (connection?.saveData) return;
  if (connection?.effectiveType && /^(slow-)?2g$/.test(connection.effectiveType)) return;

  const warm = () => void loadArenaScene().catch(() => undefined);
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  // Safari has no `requestIdleCallback`; a timeout is the same intent one
  // guarantee weaker, and being a frame late costs nothing here.
  if (idle) idle(warm);
  else window.setTimeout(warm, 1200);
}

async function deploy(
  arenaId?: string,
  mapId?: string,
  mode?: MatchMode,
): Promise<void> {
  if (busy.value) return;
  busy.value = true;

  // Both of these are network round trips and neither depends on the other, so
  // they run together. The scene's code is a one-off download per session; the
  // ticket is per sortie.
  const sceneModule = loadArenaScene();

  // The socket carries one short credential, so the access token is traded for
  // a one-shot arena ticket first. See `ArenaTicketService` on the server.
  let ticket: string;
  try {
    ticket = (await api.arenaTicket()).ticket;
  } catch {
    showNotice({
      kind: 'error',
      title: 'Could not deploy',
      lines: ['Could not get clearance for the arena. Try signing in again.'],
    });
    busy.value = false;
    return;
  }

  // Awaited before the phase switch, so a chunk that fails to arrive is refused
  // on the garage floor with a message rather than stranding the player on a
  // loading screen over a canvas that will never have a scene behind it.
  let Arena: ArenaSceneCtor;
  try {
    Arena = await sceneModule;
  } catch {
    showNotice({
      kind: 'error',
      title: 'Could not deploy',
      lines: ['Could not load the battlefield. Check your connection and try again.'],
    });
    busy.value = false;
    return;
  }

  const nickname = session.player.value?.nickname ?? '';

  // The canvas only exists once we're in the playing phase, so switch first and
  // wait a tick for Vue to put it in the DOM. The loading screen goes up in the
  // same breath, so the half-built arena is never visible behind it.
  loading.value = true;
  loadingSubtitle.value = mapName(mapId);
  loadingTutorial.value = arenaId === TUTORIAL_ARENA_ID;

  // Fullscreen and landscape, on a touch build only, and **from here** because
  // this is still inside the gesture that pressed Deploy — a browser grants a
  // presentation request only to a user gesture, so asking after the socket
  // connects or from a `whenReady` callback is asking to be refused. Awaited so
  // the viewport has settled before `TouchInputState` measures it; every branch
  // resolves, so a refusal costs the sortie nothing.
  if (wantsTouch()) await enterImmersive(document.documentElement);

  inArena.value = true;
  await new Promise((resolve) => requestAnimationFrame(resolve));

  const element = canvas.value;
  if (!element) {
    showNotice({
      kind: 'error',
      title: 'Could not deploy',
      lines: ['Could not create the render surface.'],
    });
    inArena.value = false;
    busy.value = false;
    return;
  }

  const conn = new Connection();
  const arena = new Arena(
    element,
    conn,
    (state) => {
      hud.value = state;
    },
  );
  connection.value = conn;
  scene.value = arena;

  // **Before the socket, not after.** `prepare()` settles whether this client
  // draws authored meshes or placeholders, and the answer is a network round
  // trip; the `joined` payload and the first snapshot both build tanks out of
  // it. Connecting first meant the two raced — free on localhost, lost often
  // enough in production to be reported as "placeholder tanks riding around",
  // and unrecoverable for the rest of the sortie once lost. See
  // `ArenaScene.prepare`. Nothing is shown while this waits: the loading screen
  // has been up since the phase switch above.
  await arena.prepare();
  // The wait is bounded but it is not instant, and `leave()` — a sign-out, a
  // dropped session, the player backing out — can land inside it. `leave` nulls
  // `scene`, so this is how a torn-down sortie is told from a live one, and
  // opening a battle socket for a scene that no longer exists is exactly the
  // stranded-connection bug the checks further down already guard against.
  if (scene.value !== arena) return;

  conn.connect(
    nickname,
    ticket,
    {
    onJoined: ({ playerId, spawn, spec, arenaName: joinedName, mapId: joinedMap, mode: joinedMode }) => {
      busy.value = false;
      arenaName.value = joinedName;
      // The server's answer, not the request: an arena that was already running
      // has the map it was built with, whatever the picker asked for. Which is
      // also why the loading screen re-titles itself here — asking for Ashfield
      // and landing on Ironback Ridge should say so while it loads, not after.
      loadingSubtitle.value = mapName(joinedMap) ?? loadingSubtitle.value;
      arena.onJoined(playerId, spawn, spec, joinedMap, joinedMode);
    },
    onSnapshot: (snapshot) => arena.onSnapshot(snapshot),
    onPlayerLeft: (playerId) => arena.onPlayerLeft(playerId),
    onRejected: (reason) => {
      showNotice({ kind: 'error', title: 'Could not deploy', lines: [reason] });
      leave();
    },
    onDisconnected: () => {
      if (!inArena.value) return;
      showNotice({
        kind: 'error',
        title: 'Disconnected',
        lines: ['Connection to the arena was lost.'],
      });
      leave();
    },
    },
    arenaId,
    mapId,
    mode,
  );

  await arena.start();

  // Hold the loading screen until the battlefield is worth looking at. Raced
  // against a timeout because the gate can fail to settle — see
  // `LOADING_TIMEOUT_MS` — and floored at `MIN_LOADING_MS` so a warm cache does
  // not produce a flash of artwork.
  const started = performance.now();
  await Promise.race([
    arena.whenReady(),
    new Promise((resolve) => setTimeout(resolve, LOADING_TIMEOUT_MS)),
  ]);
  const remaining = MIN_LOADING_MS - (performance.now() - started);
  if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));

  // `leave()` may have run while we waited — a rejected join, a dropped socket,
  // or the player backing out. Lowering the flag then would be harmless, but
  // checking makes it obvious that the ordering was considered.
  if (inArena.value) loading.value = false;
}

/**
 * Into the tutorial.
 *
 * An ordinary deploy with a sentinel arena id — no second code path, no second
 * socket, no local simulation, and **no overlay flag any more**. The server
 * turns `TUTORIAL_ARENA_ID` into a room private to this account on the drill
 * yard, running the `tutorial` mission mode; from that point on it is a battle
 * like any other, and the drills arrive on the snapshot exactly as a convoy's
 * leg does. The browser is no longer told it is in a lesson — it is told by the
 * same wire that tells it everything else.
 *
 * **It keeps its own entry rather than going through `api.createMission`**, and
 * that is the whole of why the room can still be free: `POST
 * /api/battle/missions` opens a *paid* room, charged by `openSession` and banked
 * by `closeSession`, and the tutorial is the one join that does neither. See
 * `CREATABLE_MISSION_KINDS`.
 *
 * The map and the mode are deliberately not passed. Both are fixed by the room
 * the server opens, and sending the garage's picker choices would be asking for
 * a battlefield the tutorial cannot be taught on.
 */
function startTutorial(): void {
  void deploy(TUTORIAL_ARENA_ID);
}

/**
 * `window.__sowDeploy(arenaId)` — join a named room, for the e2e harness.
 *
 * **This exists because a route the harness used disappeared, not because the
 * harness wants a shortcut.** `deploy()` has always taken an optional arena id,
 * and the Arena tab was one caller of it; when that tab was retired the *client*
 * kept the ability to join a room by id and the *UI* lost the way to ask for it.
 *
 * The case that needs it is `titles.mjs`, and its reason is structural rather
 * than convenient. It photographs a title badge over another player's tank at
 * 39 m, so it needs an arena with **no bots at all** — a bot has no account and
 * therefore no title, and more importantly a bot shoots. It gets that from the
 * drill yard, whose `teamSize: 1` means two humans fill the arena and nothing
 * else is dealt. `drill` is a mission map and so is deliberately absent from
 * `PICKABLE_MAPS`, which means quick match cannot reach it and every map that
 * *can* be reached tops both sides up with bots. Repointing the case at a
 * pickable map would trade a guarantee that holds by construction for one that
 * holds by luck, which is exactly what moving it to the drill yard fixed.
 *
 * The room itself is still made the way it always was, through
 * `POST /api/battle/arenas` — that route and `CreateArenaDto` are untouched, and
 * the server resolves the map slug. Only the join needed a door.
 *
 * Guarded by `import.meta.env.DEV`, like every other `__sow*` probe — but do not
 * read that as "absent from a production build", because today **it is not**.
 * Built with `yarn build` and grepped, all fifteen probes are in
 * `dist/assets/index-*.js`, this one included, and so is `__sowGl`, whose own
 * comment in `main.ts` claims a production bundle drops it.
 *
 * **Dead-code elimination is not the problem — the guard resolves to `true`.**
 * No `import.meta.env` literal survives the build, so the substitution ran; it
 * substituted the wrong value. `vite.config.ts` calls `loadEnv(mode, repoRoot,
 * '')` to pick up the machine-local ports, unfiltered, and the root `.env`
 * carries `NODE_ENV=development`; Vite routes that to `VITE_USER_NODE_ENV` and
 * resolves `isProduction === false`. Two `Vue warn` strings in the same bundle
 * are the same fact from the other end — the app ships Vue's *development*
 * build. So this is a build-configuration bug affecting the whole app, not a
 * property of any probe, and removing this one would fix nothing.
 *
 * It is safe anyway, and on purpose rather than by luck: joining a room by id is
 * a capability the *protocol* already grants every client — `join` has always
 * taken an optional arena id, the arena browser used to ask for it from the UI,
 * and the server validates the ticket, the fuel and the room's capacity exactly
 * as it does for any other join. This exposes nothing the wire did not already
 * allow. If dev probes ever do need to be genuinely absent, that is one change
 * to the build for all of them, not a special case here.
 *
 * It resolves when the deploy settles, so a case can await it instead of polling
 * for a canvas.
 */
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__sowDeploy = (arenaId: string) =>
    deploy(arenaId);
  /*
   * Forgets whether the authored models are available, so the next deploy asks
   * again over the network.
   *
   * Only `models-slow-probe` has any use for it, and without it that case
   * cannot test its own subject: the garage's hangar probes the same default
   * URL pair on the way in, `modelUrlsAvailable` memoises the `true`, and the
   * arena's probe then resolves as a microtask — which no join can lose to,
   * fix or no fix. Clearing the memo is what makes the delay the case installs
   * actually land on the deploy it is measuring.
   */
  (window as unknown as Record<string, unknown>).__sowResetModelProbe = () =>
    resetModelAvailability();
}

/** A map's display name, for the loading screen. Unknown ids simply go unnamed. */
function mapName(id?: string): string | null {
  if (!id) return null;
  return MAP_SUMMARIES.find((m) => m.id === id)?.name ?? null;
}

/**
 * The escape menu opening or closing.
 *
 * Only updates `menuOpen` — the actual suspend call is `inputSuspended`'s
 * watcher below, which composes this with the other two sources so none of
 * them can undo another. Kept as its own function rather than inlined at the
 * `@menu` binding because `menuOpen` has its own reader (`hide-cursor`,
 * template).
 */
function onMenu(open: boolean): void {
  menuOpen.value = open;
}

/**
 * Whether the input source — and with it the camera, see `setMenuOpen`'s own
 * note — should be suspended.
 *
 * Four sources, composed once rather than each calling `setMenuOpen`
 * independently: the escape menu (`menuOpen`), a decided battle
 * (`hud.outcome`, which never reverses within one battle), the pre-match
 * count-in (`hud.roundStartsInSeconds`, which does reverse — the freeze ends
 * and play begins), and the forfeit receipt (`settlement`, set by
 * `requestLeave` once a voluntary leave settles). Independent callers of
 * `setMenuOpen(true)` would each risk another's `setMenuOpen(false)`
 * un-suspending input it still owed a freeze — closing the escape menu while
 * the count-in is still running must not let the tank move, which is exactly
 * the bug this composition avoids.
 *
 * `settlement` earns its place the same way `hud.outcome` did, and its
 * absence was a real bug, not an oversight caught in review: `requestLeave`
 * sets `menuOpen.value = false` once settlement arrives (so the escape menu
 * itself closes behind the receipt), and with `settlement` not in this list
 * that was the *only* one of the three original sources still true, so
 * `inputSuspended` dropped to `false` — un-suspending input and, because
 * `lockWanted` was still set, re-requesting pointer lock the instant the
 * receipt screen appeared. The mouse was captured again under a screen with
 * nothing to aim at, and its own buttons stopped taking clicks until Escape
 * forced a re-suspend. Reported as "I have to press ESC to get the cursor
 * back after leaving."
 *
 * `requestLeave`'s own immediate `setMenuOpen(true)` is a one-way imperative
 * flow (the battle is being torn down) rather than a state this computed
 * needs to track, so it calls the scene directly and is left alone.
 */
const inputSuspended = computed(
  () =>
    menuOpen.value ||
    Boolean(hud.value.outcome) ||
    Boolean(hud.value.roundStartsInSeconds) ||
    Boolean(settlement.value),
);

watch(inputSuspended, (suspended) => scene.value?.setMenuOpen(suspended));

/**
 * Leaving a battle that is still running, which pays nothing at all — a sortie
 * is paid for finishing it.
 *
 * The socket is asked to settle *before* anything is torn down, because the
 * payout is banked when the arena releases the seat and only the server knows
 * what it came to — so this is the one moment the figures exist and there is
 * still a socket to hear them on. `leave()` itself is untouched and still the
 * only thing that disposes the scene; this just puts a receipt in front of it.
 *
 * A null settlement (a timeout, a tutorial, a socket already gone) falls straight
 * through to `leave()`: the money is banked either way, and holding a player in a
 * battle they asked to leave because a *courtesy* did not arrive would be the
 * wrong trade.
 */
async function requestLeave(): Promise<void> {
  const conn = connection.value;
  if (!conn) {
    leave();
    return;
  }
  // Suspend the input source first, so the tank stops taking orders the moment
  // the player commits — the same thing a decided battle does.
  scene.value?.setMenuOpen(true);
  const settled = await conn.leaveAndSettle();
  if (!settled) {
    leave();
    return;
  }
  settlement.value = settled;
  menuOpen.value = false;
}

function leave(): void {
  // Given back on every route out of a battle, not only the deliberate one. A
  // player who was disconnected, signed out or refused a join must not be left
  // in a fullscreen landscape garage — and `exitImmersive` is safe to call when
  // neither was ever granted, which is what makes it callable from here
  // unconditionally rather than behind a touch check.
  void exitImmersive();

  // Cleared here rather than in `requestLeave`, so every route out of a battle —
  // the receipt, a rejected join, a dropped socket, signing out — starts the next
  // sortie with no stale receipt on screen.
  settlement.value = null;
  scene.value?.dispose();
  connection.value?.disconnect();
  scene.value = null;
  connection.value = null;
  arenaName.value = null;
  busy.value = false;
  // Unconditionally, and before the phase check below: a join rejected while
  // the loading screen was up would otherwise leave it covering the garage.
  loading.value = false;
  loadingSubtitle.value = null;

  // The next sortie must not open on the last one's HUD.
  //
  // `hud` is a plain ref holding whatever the disposed scene last published —
  // its health, its kill feed, its buffs, and now its tutorial step. The arena
  // phase is entered *before* `ArenaScene` is constructed, so between the two
  // the HUD renders that stale object: for a few frames a returning player saw
  // the previous sortie's readouts, and a returning *learner* saw the finished
  // tutorial's "That is the whole of it" over a lesson that had not started.
  // Reset to the same literal the ref was created with.
  hud.value = { speedKph: 0, armourPercent: 0, latencyMs: 0, fps: 0, playerCount: 0, players: [] };

  if (inArena.value) {
    inArena.value = false;
    // Credits are paid out when the session closes, so the balance, the service
    // record and the daily quests are all stale by the time we are back in the
    // garage.
    //
    // `loadAfterSortie` rather than `load` because the payout and this read are
    // racing — it re-reads once so the title bar's balance catches the banking.
    // No popup is raised for the sortie any more: the result screen in the
    // arena is the payout's only statement, and the "Battle report" that used
    // to restate it here read as an extra reward. See `state/garage.ts`.
    if (session.signedIn.value) void garage.loadAfterSortie();
  }
}

onBeforeUnmount(leave);
</script>

<template>
  <GameHud
    v-if="previewHud"
    :hud="previewHud"
    nickname="e2e-commander-max16"
    arena-name="Ironback Ridge"
    :settlement="null"
    @leave="leavePreview"
    @request-leave="noop"
    @menu="noop"
  />
  <NotFoundScreen v-else-if="phase === 'not-found'" />

  <AdminScreen v-else-if="phase === 'admin'" :section="adminRoute!" />

  <NewsArticlePage
    v-else-if="phase === 'news' && newsRoute?.kind === 'article'"
    :slug="newsRoute.slug"
    @play="authView = 'form'"
  />
  <NewsListPage v-else-if="phase === 'news'" @play="authView = 'form'" />

  <VerifyEmailScreen
    v-else-if="phase === 'link' && deepLink?.kind === 'verify-email'"
    :token="deepLink.token"
    @done="leaveDeepLink"
  />
  <ResetPasswordScreen
    v-else-if="phase === 'link'"
    :token="deepLink!.token"
    @done="leaveDeepLink"
  />

  <div v-else-if="phase === 'restoring'" class="booting">Checking your papers…</div>

  <LandingScreen
    v-else-if="phase === 'auth' && authView === 'landing'"
    @play="authView = 'form'"
  />

  <JoinScreen v-else-if="phase === 'auth'" @back="authView = 'landing'" />

  <VerifyEmailGate v-else-if="phase === 'unverified'" />

  <GarageScreen
    v-else-if="phase === 'garage'"
    @deploy="deploy"
    @tutorial="startTutorial"
    @profile="profileOpen = true"
  />

  <!-- The pre-match count-in is deliberately not a fourth exception here: input
       is suspended the same way the menu/outcome/settlement screens are, but
       unlike them it has no button to reach — a visible cursor with nothing to
       click is a distraction on a screen whose only job is to be read. -->
  <div v-else class="stage" :class="{ 'hide-cursor': !menuOpen && !hud.outcome && !settlement }">
    <canvas ref="canvas"></canvas>
    <GameHud
      :hud="hud"
      :nickname="session.player.value?.nickname ?? ''"
      :arena-name="arenaName"
      :settlement="settlement"
      @leave="leave"
      @request-leave="requestLeave"
      @menu="onMenu"
    />
    <LoadingScreen
      v-if="loading"
      :subtitle="loadingSubtitle ?? undefined"
      :tutorial="loadingTutorial"
    />
  </div>

  <ProfileOverlay v-if="profileOpen" @close="profileOpen = false" />

  <MusicControl :arena="phase === 'playing'" />

  <!-- Here rather than in `GarageScreen` beside `ConfirmPurchase`, because Sign
       out is also on `VerifyEmailGate`, which is a sibling screen — see
       `state/confirm-sign-out.ts`. Unconditional: it draws nothing until asked. -->
  <ConfirmSignOut />

  <RotatePrompt :active="phase === 'garage' || phase === 'playing'" />

  <FullscreenPrompt v-if="phase === 'garage'" />
</template>

<style scoped>
/* The arena hides the cursor; the escape menu brings it back. `*` rather than
   the container alone because a child that sets its own cursor — a button, a
   slider, anything with `cursor: pointer` — would otherwise put the arrow back
   the moment the pointer crossed the HUD. */
.hide-cursor,
.hide-cursor * {
  cursor: none !important;
}

.booting {
  display: grid;
  place-items: center;
  height: 100%;
  color: var(--steel-400);
  font-size: 0.9rem;
}

.stage {
  position: relative;
  height: 100%;
  /*
   * The canvas already refuses `touch-action` (below), which stops the browser
   * reading a finger as a pan or a pinch-zoom — but iOS's long-press text
   * selection is a *different* gesture, gated by `user-select` and WebKit's own
   * `-webkit-touch-callout`, and neither is a form of `touch-action`. Two
   * thumbs down at once — one holding the stick, one dragging the view — is
   * exactly the shape of gesture that trips it: reported as "the whole screen
   * gets highlighted like text is selected". Scoped to `.stage` rather than the
   * whole document, since there is nothing here worth selecting and the
   * garage/chat/forms elsewhere still need ordinary text selection.
   */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

canvas {
  display: block;
  width: 100%;
  height: 100%;
  outline: none;
  touch-action: none;
}
</style>
